import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Images, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const PAGE_SIZE = 24;
const fmtKB = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDateTr = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function MediaLibrary() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [cursors, setCursors] = useState([null]);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const [q, setQ] = useState("");
    const [labelInput, setLabelInput] = useState("");
    const [renaming, setRenaming] = useState(null);
    const [renameText, setRenameText] = useState("");

    const saveLabel = async (item) => {
        try {
            await api.patch(`/media/${item.id}/label`, { label: renameText });
            toast.success("Etiket güncellendi");
            setRenaming(null);
            load(cursors[page - 1] ?? null, page);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncellenemedi");
        }
    };

    const load = useCallback(async (cursor, p, query = q) => {
        try {
            const qs = `?limit=${PAGE_SIZE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}${query ? `&q=${encodeURIComponent(query)}` : ""}`;
            const r = await api.get(`/media${qs}`);
            setData(r.data);
            setPage(p);
        } catch (e) {
            if (e.response?.status === 403) setForbidden(true);
        }
    }, [q]);

    const search = (query) => {
        setQ(query);
        setCursors([null]);
        load(null, 1, query);
    };

    useEffect(() => { load(null, 1); }, [load]);

    const next = () => {
        if (!data?.next_cursor) return;
        setCursors((c) => [...c, data.next_cursor]);
        load(data.next_cursor, page + 1);
    };
    const prev = () => {
        const target = cursors[page - 2] ?? null;
        setCursors((c) => c.slice(0, page - 1));
        load(target, page - 1);
    };

    const upload = async (files) => {
        const list = Array.from(files || []);
        if (!list.length) return;
        setUploading(true);
        try {
            if (list.length === 1) {
                const fd = new FormData();
                fd.append("file", list[0]);
                if (labelInput.trim()) fd.append("label", labelInput.trim());
                const r = await api.post("/media/upload", fd);
                toast.success(`Görsel eklendi — ${fmtKB(r.data.original_size)} → ${fmtKB(r.data.compressed_size)} (WebP)`);
            } else {
                const fd = new FormData();
                list.forEach((f) => fd.append("files", f));
                if (labelInput.trim()) fd.append("label", labelInput.trim());
                const r = await api.post("/media/upload-batch", fd);
                toast.success(`${r.data.ok_count} görsel yüklendi${r.data.error_count ? `, ${r.data.error_count} hatalı` : ""}`);
                if (r.data.errors.length) toast.warning(r.data.errors.map((e) => `${e.filename}: ${e.neden}`).join(" • "));
            }
            setCursors([null]);
            load(null, 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Yüklenemedi");
        } finally {
            setUploading(false);
        }
    };

    const del = async (item) => {
        setDeleting(item.id);
        try {
            await api.delete(`/media/${item.id}`);
            toast.success("Görsel silindi");
            load(cursors[page - 1] ?? null, page);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        } finally {
            setDeleting(null);
        }
    };

    if (forbidden) {
        return (
            <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="media-forbidden">
                <Images className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">Ortam kütüphanesi tedarikçi ve admin rolleri içindir.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="media-library-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Ortam Kütüphanesi</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {user?.role === "admin" ? "Platformdaki tüm yüklenen görseller" : "Yüklediğiniz ürün görselleri"} — WebP, maks. 1600px ile sıkıştırılır
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        value={q}
                        onChange={(e) => search(e.target.value)}
                        placeholder="Görsel adı veya etiketi ile ara..."
                        className="h-10 w-72 rounded-lg border bg-background px-3 text-sm"
                        data-testid="media-search-input"
                    />
                    <input
                        value={labelInput}
                        onChange={(e) => setLabelInput(e.target.value)}
                        placeholder="Yükleme etiketi (opsiyonel)"
                        className="h-10 w-48 rounded-lg border bg-background px-3 text-sm"
                        data-testid="media-label-input"
                    />
                    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-lg bg-terra-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terra-700 ${uploading ? "opacity-60" : ""}`} data-testid="media-upload-button">
                    <input type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="media-upload-input" />
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                    {uploading ? "Yükleniyor..." : "Görsel Yükle (çoklu)"}
                    </label>
                </div>
            </div>

            {!data ? (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                    {[...Array(12)].map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-xl bg-muted" />)}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="media-grid">
                        {data.items.map((it) => (
                            <div key={it.id} className="group relative aspect-square overflow-hidden rounded-xl border bg-card" data-testid={`media-item-${it.id}`}>
                                <img src={it.url} alt={it.original_filename || ""} className="h-full w-full object-cover" loading="lazy" />
                                {it.used_in && (
                                    <Badge className="absolute left-1.5 top-1.5 max-w-[85%] truncate border-0 bg-emerald-600/90 text-[9px] text-white" data-testid={`media-used-${it.id}`}>
                                        {it.used_in}
                                    </Badge>
                                )}
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-stone-950/70 opacity-0 transition-opacity group-hover:opacity-100">
                                    <p className="max-w-[90%] truncate px-2 text-center text-[10px] font-semibold text-white" data-testid={`media-label-${it.id}`}>{it.label || it.original_filename || "—"}</p>
                                    <p className="px-2 text-center text-[10px] text-stone-300">{fmtKB(it.size)} • {fmtDateTr(it.created_at)}</p>
                                    {renaming === it.id ? (
                                        <div className="flex gap-1 px-2">
                                            <input
                                                autoFocus
                                                value={renameText}
                                                onChange={(e) => setRenameText(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter") saveLabel(it); if (e.key === "Escape") setRenaming(null); }}
                                                placeholder="Etiket..."
                                                className="h-7 w-28 rounded border-0 px-2 text-xs text-stone-900"
                                                data-testid={`media-rename-input-${it.id}`}
                                            />
                                            <Button size="sm" variant="outline" className="h-7 border-emerald-300/40 bg-transparent text-xs text-emerald-300 hover:bg-emerald-950/50" onClick={() => saveLabel(it)} data-testid={`media-rename-save-${it.id}`}>OK</Button>
                                        </div>
                                    ) : (
                                        <Button size="sm" variant="outline" className="h-7 border-stone-300/40 bg-transparent text-xs text-stone-200 hover:bg-stone-800/60" onClick={() => { setRenaming(it.id); setRenameText(it.label || ""); }} data-testid={`media-rename-${it.id}`}>
                                            Etiketle
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 border-red-300/40 bg-transparent text-xs text-red-300 hover:bg-red-950/50 hover:text-red-200"
                                        disabled={deleting === it.id}
                                        onClick={() => del(it)}
                                        data-testid={`media-delete-${it.id}`}
                                    >
                                        {deleting === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="mr-1 h-3 w-3" />} Sil
                                    </Button>
                                </div>
                            </div>
                        ))}
                        {!data.items.length && (
                            <div className="col-span-full rounded-2xl border border-dashed p-16 text-center" data-testid="media-empty">
                                <Images className="mx-auto h-10 w-10 text-muted-foreground" />
                                <p className="mt-3 text-sm text-muted-foreground">Henüz görsel yok. İlk görselinizi yükleyin.</p>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between rounded-2xl border bg-card px-5 py-3" data-testid="media-pagination">
                        <span className="text-xs text-muted-foreground" data-testid="media-page-info">
                            Sayfa <strong className="text-foreground">{page}</strong> • Toplam {data.total} görsel
                        </span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={prev} data-testid="media-prev">
                                <ChevronLeft className="mr-1 h-4 w-4" /> Önceki
                            </Button>
                            <Button variant="outline" size="sm" disabled={!data.next_cursor} onClick={next} data-testid="media-next">
                                Sonraki <ChevronRight className="ml-1 h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
